import type { KeyboardEvent, ForwardRefExoticComponent, HTMLAttributes } from 'react'
import React, { forwardRef, useCallback, useContext, useRef } from 'react'
import { i18n } from '@fs/zion-locale'
import { css, cx } from '@linaria/core'
import { useEvent, useForwardRef } from '@fs/zion-frontend-friends'
import { useTranslation } from 'react-i18next'
import { colors } from '../theme'
import useDraggable, { limitGetDelta, stretchGetDelta } from './useDraggable'
import { DraggableContext } from './DraggableContainer'
import type { Bounds, MinMax, Rect, PartialRect, UiPoint } from './types'
import { textDirection } from '../rtl-support'

// eslint-disable-next-line @fs/zion/logical-over-directional -- This is necessary because of the way it's being used
const handleCss = css`
  --handle-size: 15px;
  position: absolute;
  left: 0;
  top: 0;
  user-select: none;
  width: var(--handle-size);
  height: var(--handle-size);
  margin-left: calc(-0.5 * var(--handle-size));
  margin-top: calc(-0.5 * var(--handle-size));

  @media (pointer: coarse) {
    --handle-size: 24px;
  }

  &:focus-visible {
    outline: 3px solid ${colors.gray60};
    outline-offset: -2px;
    border-radius: 4px;
  }
`

// Helper function to determine if an element is interactive
const isInteractiveElement = (element: Element): boolean => {
  if (!element) return false

  const tagName = element.tagName.toLowerCase()
  const role = element.getAttribute('role')

  // Common interactive elements by tag name
  const interactiveTags = ['a', 'button', 'input', 'select', 'textarea', 'summary', 'option']

  // Common interactive elements by role
  const interactiveRoles = [
    'button',
    'checkbox',
    'combobox',
    'link',
    'menuitem',
    'menuitemcheckbox',
    'menuitemradio',
    'option',
    'radio',
    'searchbox',
    'slider',
    'spinbutton',
    'switch',
    'tab',
    'textbox',
  ]

  // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
  return interactiveTags.includes(tagName) || (role && interactiveRoles.includes(role))
}

const baseHandleCss = css`
  outline: none !important;
`

const xCss = css`
  height: 100% !important;
  margin-top: 0 !important;
`

// eslint-disable-next-line @fs/zion/logical-over-directional -- This is necessary because of the way it's being used
const yCss = css`
  width: 100% !important;
  margin-left: 0 !important;
`

// eslint-disable-next-line @fs/zion/logical-over-directional -- This is necessary because of the way it's being used
const rightCss = css`
  left: 100% !important;
`

const bottomCss = css`
  top: 100% !important;
`

const KEYBOARD_INCREMENT = 20

type HandleProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Makes this drag handle not draggable.
   */
  disabled?: boolean

  /**
   * Makes this drag handle not stretchy.
   *
   * This means that the handle will not allow the container to stretch past its boundaries.
   */
  noStretchy?: boolean

  /**
   * Disables keyboard interaction with the handle via arrow keys.
   *
   * Do not use this except in cases where there is another affordance available for a blind user to interact with the container.
   */
  noKeyboard?: boolean
}

type BaseDragHandleProps = HandleProps & {
  cursor: string
  getBounds(boundingDimensions: Bounds, currentRect: Rect): MinMax
  onDragMove(delta: UiPoint, currentRect: PartialRect): PartialRect
  keyboardDescription?: string
}

type BorderHandleProps = HandleProps & {
  topLeft?: Record<string, unknown>
  top?: Record<string, unknown>
  topRight?: Record<string, unknown>
  left?: Record<string, unknown>
  right?: Record<string, unknown>
  bottomLeft?: Record<string, unknown>
  bottom?: Record<string, unknown>
  bottomRight?: Record<string, unknown>
}

// This is declared outside of the component that uses it, so that it will keep referential integrity.
// It's a default value of something, so that this code doesn't complain when a rect isn't provided.
const emptyRect: Rect = { x: 0, y: 0, width: 0, height: 0 }

export const BaseDragHandle = forwardRef<HTMLDivElement, BaseDragHandleProps>(
  (
    {
      getBounds: getDragHandleBounds,
      onDragMove,
      disabled,
      cursor,
      style,
      noStretchy,
      noKeyboard = false,
      keyboardDescription,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const draggableRef = useForwardRef(ref)
    const startRef = useRef(emptyRect)

    const handleGetBounds = useEvent(getDragHandleBounds)
    const handleDragMove = useEvent(onDragMove)

    // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
    const { dragStart, dragUpdate, dragEnd, getBounds: containerGetBounds, transform } = useContext(DraggableContext)

    const onDragStartHandler = useCallback(() => {
      startRef.current = dragStart()
    }, [dragStart])

    const onDragMoveHandler = useCallback(
      (delta: UiPoint) => {
        const startRect = startRef.current
        const change = handleDragMove(delta, startRect)
        dragUpdate(change)
      },
      [dragUpdate, handleDragMove]
    )

    const onDragEndHandler = useCallback(
      (delta: UiPoint) => {
        const change = handleDragMove(delta, startRef.current)
        dragEnd(change)
      },
      [handleDragMove, dragEnd]
    )

    const getBoundsHandler = useCallback(
      () => handleGetBounds(containerGetBounds(), startRef.current),
      [containerGetBounds, handleGetBounds]
    )

    useDraggable({
      draggableRef,
      getBounds: getBoundsHandler,
      onDragStart: onDragStartHandler,
      onDragMove: onDragMoveHandler,
      onDragEnd: onDragEndHandler,
      cursor,
      disabled,
      noStretchy,
    })

    const keyboardHandler = useEvent((event: KeyboardEvent<HTMLDivElement>): void => {
      onKeyDown?.(event)
      const { key, defaultPrevented, altKey, metaKey, ctrlKey, target, currentTarget } = event
      if (disabled || noKeyboard || defaultPrevented || altKey || metaKey || ctrlKey) return

      // Check if the event target is an interactive element
      if (target instanceof Element && isInteractiveElement(target) && target !== currentTarget) return

      // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
      let delta: UiPoint = null
      if (keyboardDescription) {
        switch (key) {
          case 'ArrowUp':
            delta = { x: 0, y: -KEYBOARD_INCREMENT }
            break
          case 'ArrowDown':
            delta = { x: 0, y: KEYBOARD_INCREMENT }
            break
          case 'ArrowLeft':
            delta = { x: -KEYBOARD_INCREMENT, y: 0 }
            break
          case 'ArrowRight':
            delta = { x: KEYBOARD_INCREMENT, y: 0 }
            break
          default:
          // no-op
        }
      }

      if (delta) {
        const startRect = dragStart()
        const bounds = handleGetBounds(containerGetBounds(), startRect)
        const destinationPoint = limitGetDelta({ start: { x: 0, y: 0 }, end: delta, ...bounds })
        const stretchyPoint = stretchGetDelta({ start: { x: 0, y: 0 }, end: delta, ...bounds })

        const change = handleDragMove(stretchyPoint, startRect)
        const springBack = handleDragMove(destinationPoint, startRect)

        transform({ change, springBack, shouldAnimate: !noStretchy })
      }
    })

    const noTabIndex = disabled || noKeyboard || !keyboardDescription

    // Check if this is a MoveHandle by looking for data-move-handle
    const isMoveHandle = 'data-move-handle' in props

    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- There's no good role for resize/move handles. */}
        <div
          role={isMoveHandle || noTabIndex ? undefined : 'button'}
          onKeyDown={keyboardHandler}
          aria-label={!isMoveHandle ? keyboardDescription : undefined}
          aria-description={isMoveHandle ? keyboardDescription : undefined}
          tabIndex={noTabIndex ? undefined : 0}
          ref={draggableRef}
          data-drag-handle
          style={{ ...style, cursor: disabled ? undefined : cursor }}
          {...props}
        />
      </>
    )
  }
)

export const MoveHandle = forwardRef<HTMLDivElement, HandleProps>(({ className, ...props }, ref) => {
  const { t } = useTranslation()
  return (
    <BaseDragHandle
      {...props}
      ref={ref}
      getBounds={(b, { x, y, width, height }) => ({
        min: { x: b.boundary.x - x, y: b.boundary.y - y },
        max: { x: b.boundary.x + b.boundary.width - x - width, y: b.boundary.y + b.boundary.height - y - height },
      })}
      onDragMove={(delta, { x, y }) => ({ x: x + delta.x, y: y + delta.y })}
      cursor="move"
      keyboardDescription={t('draggable.move-handle.label')}
      className={cx(className, baseHandleCss)}
      /* data-move-handle is needed because some draggable containers want
       * to put a focus ring around the entire container when the move handle is focused
       */
      data-move-handle
    />
  )
})

type LimitValue = (x: number, width: number, leftBound: number, rightBound: number, minWidth: number) => number
type UpdateValue = (x: number, width: number, delta: number) => number | undefined

const noMin = (): number => -Infinity
const noMax = (): number => Infinity
const noChange = (): undefined => undefined

type MakeHandle = {
  minX?: LimitValue
  minY?: LimitValue
  maxX?: LimitValue
  maxY?: LimitValue
  setX?: UpdateValue
  setY?: UpdateValue
  setWidth?: UpdateValue
  setHeight?: UpdateValue
  keyboardDescription?: string
}

const makeResizeHandle = (
  {
    minX = noMin,
    minY = noMin,
    maxX = noMax,
    maxY = noMax,
    setX = noChange,
    setY = noChange,
    setWidth = noChange,
    setHeight = noChange,
    ...rest
  }: MakeHandle,
  cursor: string,
  classNames: string[] = []
): ForwardRefExoticComponent<HandleProps> =>
  forwardRef<HTMLDivElement, HandleProps>(({ className, ...props }, ref) => (
    <BaseDragHandle
      {...rest}
      {...props}
      ref={ref}
      getBounds={({ boundary, minHeight, minWidth }, { x, y, width, height }) => ({
        min: {
          x: minX(x, width, boundary.x, boundary.width, minWidth),
          y: minY(y, height, boundary.y, boundary.height, minHeight),
        },
        max: {
          x: maxX(x, width, boundary.x, boundary.width, minWidth),
          y: maxY(y, height, boundary.y, boundary.height, minHeight),
        },
      })}
      onDragMove={(delta, { x, y, width, height }) => ({
        // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
        x: setX(x, width, delta.x),
        // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
        y: setY(y, height, delta.y),
        // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
        width: setWidth(x, width, delta.x),
        // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
        height: setHeight(y, height, delta.y),
      })}
      className={cx(handleCss, ...classNames, className)}
      cursor={cursor}
    />
  ))

const setTopHandleY: UpdateValue = (y, _, deltaY) => y + deltaY
// The text direction needs to be ascertained at render time, not when this file is imported and the config objects are created.
const setHorizontalHandleXIfDirectionIs: (targetTextDirection: 'ltr' | 'rtl') => UpdateValue =
  (targetTextDirection) => (x, _, deltaX) => (textDirection() === targetTextDirection ? x + deltaX : undefined)
const setLeftHandleWidth: UpdateValue = (_, width, deltaX) => width - deltaX
const setRightHandleWidth: UpdateValue = (_, width, deltaX) => width + deltaX

const limitLeftMin: LimitValue = (x, _, leftBound) => leftBound - x
const limitLeftMax: LimitValue = (_, width, _1, _2, minWidth) => width - minWidth

const limitRightMin: LimitValue = (_, width, _1, _2, minWidth) => minWidth - width
const limitRightMax: LimitValue = (x, width, leftBound, rightBound) => leftBound + rightBound - x - width

const topConfig = {
  minY: limitLeftMin,
  maxY: limitLeftMax,
  setY: setTopHandleY,
  setHeight: setLeftHandleWidth,
}

const bottomConfig = {
  minY: limitRightMin,
  maxY: limitRightMax,
  setHeight: setRightHandleWidth,
}

const leftConfig = {
  minX: limitLeftMin,
  maxX: limitLeftMax,
  setX: setHorizontalHandleXIfDirectionIs('ltr'),
  setWidth: setLeftHandleWidth,
}

const rightConfig = {
  minX: limitRightMin,
  maxX: limitRightMax,
  setX: setHorizontalHandleXIfDirectionIs('rtl'),
  setWidth: setRightHandleWidth,
}

export const TopResizeHandle = makeResizeHandle(topConfig, 'ns-resize', [yCss])
export const BottomResizeHandle = makeResizeHandle(bottomConfig, 'ns-resize', [yCss, bottomCss])
export const LeftResizeHandle = makeResizeHandle(leftConfig, 'ew-resize', [xCss])
export const RightResizeHandle = makeResizeHandle(rightConfig, 'ew-resize', [xCss, rightCss])

export const TopLeftResizeHandle = makeResizeHandle({ ...topConfig, ...leftConfig }, 'nwse-resize')
export const TopRightResizeHandle = makeResizeHandle({ ...topConfig, ...rightConfig }, 'nesw-resize', [rightCss])
export const BottomLeftResizeHandle = makeResizeHandle({ ...bottomConfig, ...leftConfig }, 'nesw-resize', [bottomCss])
export const BottomRightResizeHandle = makeResizeHandle(
  { ...bottomConfig, ...rightConfig, keyboardDescription: i18n.t('draggable.resize-handle.label') },
  'nwse-resize',
  [bottomCss, rightCss]
)

export const BorderResizeHandles = ({
  topLeft,
  top,
  topRight,
  left,
  right,
  bottomLeft,
  bottom,
  bottomRight,
}: BorderHandleProps): JSX.Element => (
  <>
    <TopResizeHandle {...top} />
    <BottomResizeHandle {...bottom} />
    <LeftResizeHandle {...left} />
    <RightResizeHandle {...right} />
    <TopLeftResizeHandle {...topLeft} />
    <TopRightResizeHandle {...topRight} />
    <BottomLeftResizeHandle {...bottomLeft} />
    <BottomRightResizeHandle {...bottomRight} />
  </>
)
